import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  TextField,
  Button,
  Autocomplete,
  Typography,
  Box,
  useTheme,
  Card,
  FormControl,
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import SaveIcon from "@mui/icons-material/Save";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import {
  calculateAndFormatNoAbs,
  calculateAndFormatNoAbsInt,
  findBrokerAbb,
  findBrokerId,
  findStrategy,
  fourDecimal,
  oneDecimal,
  twoDecimal,
} from "../../component/Methods";
import { GetAllocation } from "../../component/Methods/dashboardCalculations";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../Redux/Reducers/uiSlice";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { colorPalette } from "../../component/Methods/colors";
import {
  sortData,
  sortFunds,
  sortStrategies,
  updateLongShort,
  updateTotals,
} from "../../component/Methods/fundAlloc";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { useFundAllocatorState } from "../../hooks/useOptimizedState";
import Existing from "./Existing";
import Target from "./Target";
import FundDetails from "./FundDetails";
import StrategiesAllocation from "./StrategiesAllocation";
import FundAllocatorContentState from "./FundAllocatorContentState";
import { useDispatch } from "react-redux";


const AllocationDetails = () => {
  const theme = useTheme();
  const [activeAum, setActiveAum] = useState(0);
  const [isSma, setIsSma] = useState(false);
  const [matchedAllocations, setMatchedAllocations] = useState([]);
  const [isAllocationValid, setIsAllocationValid] = useState(true);
  const [allocation, setAllocation] = useState({});
  const [fundId, setFundId] = useState();
  const [oldData, setOldData] = useState([]);
  const [existing, setExisting] = useState([]);
  const [fundAum, setFundAum] = useState(0);
  const [totals, setTotals] = useState([]);
  const [fundVal, setFundVal] = useState(null);
  const [Strategies, setStrategy] = useState([]);
  const [StrategiesLowAum, setStrategyLowAum] = useState([]);
  const [StrategiesCombined, setStrategyCombined] = useState([]);
  const [autoCompletedValue, setautoCompletedValue] = useState([]);
  const [editableValue, setEditableValue] = useState(0);
  const [Piedata, setPieData] = useState([]);
  const [PiedataTarget, setPieDataTarget] = useState([]);
  const [editableData, setEditableData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDoneEditing, setIsDoneEditing] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [hasEditedAllocation, setHasEditedAllocation] = useState(false);
  const [isFundLoading, setIsFundLoading] = useState(false);

  // Ref to track if we should preserve editableData
  const preserveEditableDataRef = useRef(false);
  const strategiesSplitDoneRef = useRef(false);

  useEffect(() => {
    setActiveAum(fundAum);
  }, [fundAum]);
  const {
    marketData,
    livePrices,
    markets,
    accountingData,
    funds,
    strategies,
    brokers,
    managers,
    lots,
  } = useFundAllocatorState();

  const dispatch = useDispatch();
  const sortedFunds = sortFunds(funds);
  const sortedData = sortData(brokers);

  useEffect(() => {
    if (Strategies.length > 0) {
      const highAumStrategies = [];
      const lowAumStrategies = [];

      Strategies.forEach((strategy) => {
        const allocationData = allocation?.items?.[strategy.strategyId];
        const totalAllocation = allocationData?.totalAllocation || 0;
        const strategyAum = strategy.aum || 0;

        if (totalAllocation > 0 || strategyAum >= 10) {
          highAumStrategies.push(strategy);
        } else {
          lowAumStrategies.push(strategy);
        }
      });

      const byNameAsc = (a, b) =>
        (a?.name || "")
          .toLowerCase()
          .localeCompare((b?.name || "").toLowerCase());

      setStrategyLowAum(lowAumStrategies.sort(byNameAsc));
      setStrategyCombined(highAumStrategies.sort(byNameAsc));
      strategiesSplitDoneRef.current = true;
    } else {
      setStrategyCombined([]);
      setStrategyLowAum([]);
      strategiesSplitDoneRef.current = false;
    }
  }, [Strategies, allocation]);

  const getFund = (fundId) => {
    const fundData = funds?.find((i) => i?.fundId === fundId);
    if (fundData?.allocation) {
      const allocation = {
        items: Object.fromEntries(
          Object.entries(fundData.allocation.items).map(([key, value]) => [
            key,
            {
              ...value,

              broker: findBrokerAbb(value?.broker, brokers),
              percentLong: (value?.percentLong ?? value?.percentlong) * 100,
              percentShort: (value?.percentShort ?? value?.percentshort) * 100,
              totalAllocation:
                (value?.totalAllocation ?? value?.totalallocation) * 100,
              ignoreWhenRebalancing:
                value?.ignoreWhenRebalancing ?? value?.ignorewhenrebalancing,
              byPass: value?.byPass ?? value?.bypass,
            },
          ])
        ),
      };
      setAllocation(allocation);
    } else {
      setAllocation(null);
    }
    if (managers[0]?.smas[fundId]) {
      setIsSma(true);
    } else {
      setIsSma(false);
    }
  };

  useEffect(() => {
    if (managers) {
      getFund(fundId);
    }
  }, [fundId, managers, funds]);

  useEffect(() => {
    if ((allocation && allocation.items) || StrategiesCombined.length > 0) {
      // If user has edited allocations and clicked "Done Editing", use matchedAllocations
      // Otherwise use the original allocation from the fund
      const fundAllocations =
        hasEditedAllocation && matchedAllocations.length > 0
          ? matchedAllocations.reduce((acc, item) => {
              acc[item.strategyId] = {
                totalAllocation: item.totalAllocation,
                percentLong: item.percentLong,
                percentShort: item.percentShort,
                broker: item.broker,
                account: item.account,
                ignoreWhenRebalancing: item.ignoreWhenRebalancing,
                byPass: item.byPass,
              };
              return acc;
            }, {})
          : allocation?.items || {};

      const sortedStrategies = [...StrategiesCombined].sort((a, b) =>
        (a?.name || "")
          .toLowerCase()
          .localeCompare((b?.name || "").toLowerCase())
      );

      const strategyColorMap = sortedStrategies?.reduce(
        (acc, strategy, index) => {
          const color = colorPalette[index % colorPalette.length];
          acc[strategy?.name] = color;
          return acc;
        },
        {}
      );

      const newAllocations = sortedStrategies?.map((strategy) => {
        const allocationData = fundAllocations[strategy.strategyId];

        if (allocationData) {
          const {
            ignoreWhenRebalancing,
            percentLong,
            percentShort,
            totalAllocation,
            broker,
            account,
            byPass,
            ...rest
          } = allocationData;

          const { long, short } = GetAllocation(
            fundId,
            strategy?.name,
            lots,
            accountingData,
            funds,
            strategies,
            livePrices,
            markets
          );

          const existingPercent =
            fundAum > 0
              ? calculateAndFormatNoAbs((strategy?.aum / fundAum) * 100)
              : 0;

          const fundAumCal = parseFloat(activeAum) || 0;
          const alcTarget = (fundAumCal * Number(totalAllocation)) / 100;
          const alcExisting = strategy?.aum;
          const formattedLong =
            strategy?.aum > 0 ? twoDecimal((long / strategy?.aum) * 100) : 0;
          const formattedShort =
            strategy?.aum > 0
              ? twoDecimal((Math.abs(short) / strategy?.aum) * 100)
              : 0;
          const strategyColor = strategyColorMap[strategy?.name];
          return {
            ...strategy,
            ...rest,
            byPass: Boolean(byPass),
            ignoreWhenRebalancing: Boolean(ignoreWhenRebalancing),
            existingAllocation: alcExisting
              ? calculateAndFormatNoAbsInt(alcExisting)
              : 0,
            allocation: alcTarget ? calculateAndFormatNoAbsInt(alcTarget) : "",
            totalAllocation: totalAllocation ? Number(totalAllocation) : "",
            percentShort: percentShort
              ? oneDecimal(-Math.abs(Number(percentShort)))
              : "",

            percentLong: percentLong ? oneDecimal(Number(percentLong)) : "",
            existingPercent: Number.isFinite(Number(existingPercent))
              ? Number(existingPercent)
              : 0,
            long: Number.isFinite(Number(formattedLong))
              ? Number(formattedLong)
              : 0,
            short: Number.isFinite(Number(formattedShort))
              ? Number(formattedShort)
              : 0,
            broker: broker,
            account: account,
            color: strategyColor,
          };
        } else {
          const { long, short } = GetAllocation(
            fundId,
            strategy?.name,
            lots,
            accountingData,
            funds,
            strategies,
            livePrices,
            markets
          );

          const formattedLong =
            strategy?.aum > 0 ? oneDecimal((long / strategy?.aum) * 100) : 0;
          const formattedShort =
            strategy?.aum > 0
              ? oneDecimal((Math.abs(short) / strategy?.aum) * 100)
              : 0;

          const fundAumCal = parseFloat(activeAum) || 0;
          const existingPercent =
            fundAumCal > 0 ? (strategy?.aum / fundAumCal) * 100 : 0;
          const strategyColor = strategyColorMap[strategy?.name];

          return {
            ...strategy,
            broker: "",
            account: "",
            existingAllocation: strategy?.aum
              ? calculateAndFormatNoAbsInt(strategy?.aum)
              : 0,
            allocation: "",
            totalAllocation: "",
            percentShort: "",
            ignoreWhenRebalancing: false,
            byPass: false,
            percentLong: "",
            existingPercent: Number.isFinite(Number(existingPercent))
              ? Number(existingPercent)
              : 0,
            long: Number.isFinite(Number(formattedLong))
              ? Number(formattedLong)
              : 0,
            short: Number.isFinite(Number(formattedShort))
              ? Number(formattedShort)
              : 0,
            color: strategyColor,
          };
        }
      });
      const pieExisting = newAllocations?.map((strategy) => ({
        name: strategy?.name,
        value: Number(strategy?.existingPercent) || 0,
        color: strategy.color,
      }));

      const pieTarget = newAllocations?.map((strategy) => ({
        name: strategy?.name,
        value: Number(strategy?.totalAllocation) || 0,
        color: strategy.color,
      }));
      setPieData(pieExisting);
      setPieDataTarget(pieTarget);
      setMatchedAllocations(newAllocations);

      // Preserve editableData changes when adding new strategies
      setEditableData((prevEditableData) => {
        // If we're preserving changes (ref is true), merge the edited data
        if (preserveEditableDataRef.current && prevEditableData.length > 0) {
          // Create a map of previous edits by strategyId
          const editMap = new Map();
          prevEditableData.forEach((item) => {
            editMap.set(item.strategyId, item);
          });

          // Merge newAllocations with previous edits
          const mergedData = newAllocations.map((newAlloc) => {
            const previousEdit = editMap.get(newAlloc.strategyId);

            // If this strategy has edits, use the edited values
            if (previousEdit) {
              return {
                ...newAlloc,
                // Preserve edited fields
                allocation: previousEdit.allocation,
                totalAllocation: previousEdit.totalAllocation,
                percentLong: previousEdit.percentLong,
                percentShort: previousEdit.percentShort,
                broker: previousEdit.broker,
                account: previousEdit.account,
                ignoreWhenRebalancing: previousEdit.ignoreWhenRebalancing,
                byPass: previousEdit.byPass,
              };
            }

            // New strategy, use default values
            return newAlloc;
          });

          return mergedData;
        }

        // No pending changes, use new allocations
        return newAllocations;
      });

      setExisting(newAllocations);
      setTotals(updateTotals(newAllocations, editableValue));
      setOldData(updateLongShort(newAllocations, fundAum));
      if (fundId && fundVal) {
        setIsFundLoading(false);
      }
    } else {
      setMatchedAllocations([]);
      setExisting([]);
      setOldData([
        { name: "Total for Long", changeValue: 0, changeAllocation: 0 },
        { name: "Total for Short", changeValue: 0, changeAllocation: 0 },
        { name: "Total Gross", changeValue: 0, changeAllocation: 0 },
        { name: "Total Net", changeValue: 0, changeAllocation: 0 },
      ]);
      setTotals([
        { changeValue: 0, changeAllocation: 0 },
        { changeValue: 0, changeAllocation: 0 },
        { changeValue: 0, changeAllocation: 0 },
        { changeValue: 0, changeAllocation: 0 },
      ]);
      setPieData([]);
      setPieDataTarget([]);
      if (
        fundId &&
        fundVal &&
        strategiesSplitDoneRef.current &&
        Strategies.length > 0
      ) {
        setIsFundLoading(false);
      }
    }
  }, [
    allocation,
    Strategies,
    managers,
    activeAum,
    fundAum,
    fundId,
    lots,
    accountingData,
    funds,
    fundVal,
    marketData,
    StrategiesCombined,
  ]);

  useEffect(() => {
    if (fundAum) {
      setEditableValue(fundAum);
    }
  }, [fundAum]);

  // Helper function to reset all fund-related state
  const resetFundState = (includeFundId = true) => {
    if (includeFundId) setFundId();
    setFundAum(0);
    setActiveAum(0);
    setEditableValue(0);
    setMatchedAllocations([]);
    setExisting([]);
  };

  // Helper function to set strategies with zero AUM
  const setStrategiesWithZeroAum = () => {
    const allStrategiesWithZeroAum = strategies?.map((strategy) => ({
      ...strategy,
      aum: 0,
    }));
    setStrategy(allStrategiesWithZeroAum);
    setMatchedAllocations([]);
    setExisting([]);
  };

  const handleFundChange = (newValue) => {
    setFundVal(newValue);
    setHasChanges(false);
    preserveEditableDataRef.current = false;
    setHasEditedAllocation(false);
    setShowWarning(false);
    strategiesSplitDoneRef.current = false;

    // Reset all fund-related states immediately when changing funds
    setFundAum(0);
    setActiveAum(0);
    setEditableValue(0);
    setMatchedAllocations([]);
    setEditableData([]);
    setExisting([]);
    setStrategy([]);
    setStrategyCombined([]);
    setStrategyLowAum([]);

    if (newValue == null) {
      setAllocation(null);
      setautoCompletedValue([]);
      setIsFundLoading(false);
      setFundId(null);
      resetFundState(false);
      return;
    }

    setIsFundLoading(true);

    const item = funds?.find((e) => e.abbreviation === newValue);
    if (item?.accounts && Object.keys(item?.accounts).length) {
      const transformedAccounts = Object.keys(item.accounts)
        .map((key) => ({
          name: key,
          custodian: item.accounts[key].custodian,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setautoCompletedValue(transformedAccounts);
    } else {
      setautoCompletedValue([]);
    }

    if (item && item?.fundId) {
      setFundId(item?.fundId);
      getFund(item?.fundId);
      const foundFund = accountingData[0]?.funds?.find((row) => {
        const fundIdValue =
          typeof row?.fund === "string" ? row?.fund : row?.fund?.fundId;
        return fundIdValue === item?.fundId;
      });

      if (foundFund) {
        setFundAum(foundFund?.aum);
        setActiveAum(foundFund?.aum);
        setEditableValue(foundFund?.aum);

        if (
          foundFund?.strategyAUMs &&
          Object.keys(foundFund.strategyAUMs).length > 0
        ) {
          const strategyAUMsData = Object.keys(foundFund.strategyAUMs)
            .filter((strategyId) =>
              strategies.some((strategy) => strategy?.strategyId === strategyId)
            )
            .map((strategyId) => {
              return {
                strategyId: strategyId,
                name: findStrategy(strategyId, strategies),
                aum: foundFund?.strategyAUMs[strategyId],
              };
            });

          if (strategyAUMsData?.length > 0) {
            const mergedStrategies = strategies?.map((strategy) => {
              const matchingAUM = strategyAUMsData?.find(
                (aumData) => aumData.strategyId === strategy.strategyId
              );

              return {
                ...strategy,
                aum: matchingAUM ? matchingAUM.aum : 0,
              };
            });

            setStrategy(mergedStrategies);
            setMatchedAllocations([]);
            setExisting([]);
          } else {
            setStrategiesWithZeroAum();
          }
        } else {
          setStrategiesWithZeroAum();
        }
      } else {
        setStrategiesWithZeroAum();
      }
    } else {
      resetFundState();
    }
  };

  useEffect(() => {
    if (fundId) {
    }
  }, [fundId]);

  const handleDoneEditing = () => {
    setIsDoneEditing((prev) => !prev);
    setShowWarning(true);
    preserveEditableDataRef.current = false;
    setHasEditedAllocation(true); // Mark that allocations have been edited

    setMatchedAllocations((prevAllocations) => {
      const updatedAllocations = [...prevAllocations];
      updatedAllocations.forEach((allocation, index) => {
        updatedAllocations[index] = {
          ...updatedAllocations[index],
          ...editableData[index],
        };
      });
      setTotals(updateTotals(updatedAllocations, editableValue));

      const pieTarget = updatedAllocations.map((strategy) => ({
        name: strategy.name,
        value: Number(strategy.totalAllocation) || 0,
        color: strategy?.color,
      }));

      setPieDataTarget(pieTarget);
      setHasChanges(false);
      return updatedAllocations;
    });
  };

  const handleSave = () => {
    setShowWarning(false);
    setHasEditedAllocation(false);
    const totalAllocationSum = matchedAllocations.reduce((sum, strategy) => {
      const totalAllocation = parseFloat(strategy.totalAllocation);
      return sum + (isNaN(totalAllocation) ? 0 : totalAllocation / 100);
    }, 0);

    setIsAllocationValid(true);

    const formattedAllocations = matchedAllocations.reduce((acc, strategy) => {
      const {
        strategyId,
        account,
        totalAllocation,
        percentShort,
        percentLong,
        ignoreWhenRebalancing,
        broker,
        byPass,
      } = strategy;

      const parsedTotalAllocation = parseFloat(totalAllocation);
      const parsedPercentShort = parseFloat(percentShort);
      const parsedPercentLong = parseFloat(percentLong);

      const baseObj = {
        account: account || autoCompletedValue[0]?.name,
        totalAllocation: isNaN(parsedTotalAllocation)
          ? 0
          : parsedTotalAllocation / 100,
        percentShort: isNaN(parsedPercentShort) ? 0 : parsedPercentShort / 100,
        percentLong: isNaN(parsedPercentLong) ? 0 : parsedPercentLong / 100,
        ignoreWhenRebalancing: ignoreWhenRebalancing,
        byPass: byPass,
        broker: findBrokerId(broker || sortedData[0]?.abbreviation, brokers),
      };

      acc[strategyId] = baseObj;

      return acc;
    }, {});
    const finalData = {
      action: "set_fund_allocation",
      data: {
        allocations: formattedAllocations,
        fund: fundId,
        sma: isSma,
        targetedAUM: editableValue,
      },
    };
    sendWebSocketMessage(JSON.stringify(finalData));
    dispatch(addActiveRequests("set_fund_allocation"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Updating Fund Allocation"));
  };

  const totalAllocationSumTarget = matchedAllocations?.reduce(
    (sum, strategy) => {
      return sum + (parseFloat(strategy?.totalAllocation) || 0);
    },
    0
  );
  const totalAllocationSumExisting = matchedAllocations?.reduce(
    (sum, strategy) => {
      return sum + (parseFloat(strategy?.existingPercent) || 0);
    },
    0
  );

  const hasFundSelected = Boolean(fundVal && fundId);
  const showFundLoader =
    hasFundSelected &&
    (isFundLoading ||
      (StrategiesCombined.length > 0 && matchedAllocations.length === 0));

  if (livePrices.length === 0) {
    return (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
              p: 6,
              backgroundColor: theme.palette.background.paper,
              textAlign: "center",
              minHeight: "400px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                backgroundColor: theme.palette.warning.light,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
                color: theme.palette.warning.contrastText,
              }}
            >
              <QueryStatsIcon
                sx={{
                  fontSize: "60px",
                  fontWeight: "bold",
                }}
              />
            </Box>

            {/* Main Message */}
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                textAlign: "center",
                fontFamily: "clashGrotesk !important",
                color: theme.palette.text.primary,
                mb: 2,
              }}
            >
              Market Data Unavailable
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                textAlign: "center",
                fontFamily: "clashGrotesk !important",
                color: theme.palette.text.secondary,
                mb: 4,
                maxWidth: "600px",
                lineHeight: 1.6,
              }}
            >
              Stock prices and market data are currently not available. Please
              use the "Fetch Market Data" button in the top navigation bar to
              retrieve the latest market information.
            </Typography>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
              p: 3,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={sortedFunds || []}
                    value={fundVal ?? null}
                    autoSelect
                    onChange={(e, newValue) => handleFundChange(newValue)}
                    getOptionLabel={(option) => option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Fund to Manage"
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flex: "0 0 auto",
                    minWidth: "300px",
                    zIndex: 1000,
                  }}
                >
                  {hasChanges ? (
                    <Button
                      variant="contained"
                      startIcon={<CachedIcon />}
                      onClick={handleDoneEditing}
                      sx={{
                        backgroundColor: "#4CAF50",
                        color: "white",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "#45a049",
                        },
                      }}
                    >
                      Done Editing
                    </Button>
                  ) : (
                    <>
                      {showWarning && (
                        <Typography
                          sx={{
                            backgroundColor: theme.palette.warning.light,
                            color: theme.palette.warning.contrastText,
                            padding: "8px",
                            borderRadius: "2px",
                            marginRight: 2,
                          }}
                        >
                          ⚠️ Please click SAVE CHANGES to update allocation
                        </Typography>
                      )}

                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={!fundVal || !fundId}
                        sx={{
                          borderRadius: 1,
                          textTransform: "none",
                          fontWeight: 500,
                          backgroundColor: theme.palette.success.main,
                          "&:hover": {
                            backgroundColor: theme.palette.success.dark,
                          },
                        }}
                      >
                        Save Changes
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <FundAllocatorContentState
          hasFundSelected={hasFundSelected}
          showFundLoader={showFundLoader}
        >
          <>
        {/* Charts Section */}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
              p: 3,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography
              sx={{
                fontSize: "25px",
                fontWeight: 900,
                color: theme.palette.text.primary,
                mb: 3,
              }}
            >
              Fund Summary
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                height: "100%",
                alignItems: "flex-start",
                flexWrap: "nowrap",
                gap: 1,
                overflowX: "auto",
              }}
            >
              <FundDetails totals={totals} oldData={oldData} fundAum={fundAum} />
              <Existing totals={totals} oldData={oldData} fundAum={fundAum} />
              <Target
                totals={totals}
                oldData={oldData}
                editableValue={editableValue}
                setEditableValue={setEditableValue}
                setActiveAum={setActiveAum}
                fundAum={fundAum}
              />

              {/* Donut Charts */}
              {Piedata.length > 0 && PiedataTarget.length > 0 && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      minWidth: "280px",
                      flexShrink: 0,
                      marginTop: -4,
                    }}
                  >
                    <PieChart width={280} height={280}>
                      <Pie
                        data={Piedata}
                        cx={140}
                        cy={140}
                        innerRadius={55}
                        outerRadius={110}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {Piedata.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry?.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                    {totalAllocationSumExisting > 0 && (
                      <>
                        <Typography
                          sx={{
                            fontSize: "20px",
                            fontWeight: 900,
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            mt: 1,
                          }}
                        >
                          Existing Allocation
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: theme.palette.text.secondary,
                            textAlign: "center",
                          }}
                        >
                          {`Total Allocation: ${totalAllocationSumExisting?.toFixed(
                            1
                          )}%`}
                        </Typography>
                      </>
                    )}
                  </Box>
                </>
              )}

              {PiedataTarget.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: "280px",
                    flexShrink: 0,
                    marginTop: -4,
                  }}
                >
                  <PieChart width={280} height={280}>
                    <Pie
                      data={PiedataTarget}
                      cx={140}
                      cy={140}
                      innerRadius={55}
                      outerRadius={110}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {PiedataTarget.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                  {totalAllocationSumTarget > 0 && (
                    <>
                      <Typography
                        sx={{
                          fontSize: "20px",
                          fontWeight: 900,
                          color: theme.palette.text.primary,
                          textAlign: "center",
                          mt: 1,
                        }}
                      >
                        Target Allocation
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: theme.palette.text.secondary,
                          textAlign: "center",
                        }}
                      >
                        {`Total Allocation: ${totalAllocationSumTarget?.toFixed(
                          1
                        )}%`}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Fund Allocation Section */}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
              p: 3,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography
              sx={{
                fontSize: "25px",
                fontWeight: 900,
                color: theme.palette.text.primary,
                mb: 3,
              }}
            >
              Strategy Allocations
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
              }}
            >
              <StrategiesAllocation 
                custodians={autoCompletedValue}
                sortedData={sortedData}
                matchedAllocations={matchedAllocations}
                setMatchedAllocations={setMatchedAllocations}
                fundAum={fundAum}
                fundId={fundId}
                setPieDataTarget={setPieDataTarget}
                editableValue={editableValue}
                editableData={editableData}
                setEditableData={setEditableData}
                StrategiesLowAum={StrategiesLowAum}
                setStrategyCombined={setStrategyCombined}
                setStrategyLowAum={setStrategyLowAum}
                setHasChanges={setHasChanges}
                onDoneEditing={isDoneEditing}
                strategies={strategies}
                StrategiesCombined={StrategiesCombined}
                preserveEditableDataRef={preserveEditableDataRef}
              />
            </Box>
          </Card>
        </Grid>
          </>
        </FundAllocatorContentState>
      </Grid>
    </>
  );
};

export default AllocationDetails;
